import {
  HTMLAttributes,
  ReactNode,
  memo,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/css';
import toLowerCase from 'utils/toLowerCase';
import { useTranslate } from '@tolgee/react';
import { Button, Dropdown, Input, Menu, message, Select } from 'antd';
import { CloseOutlined, DownOutlined } from '@ant-design/icons';
import { ProductListParamsKeywordType } from 'services/api/conversation';
import Empty from 'components/Empty';
import ProductComponent from './ProductComponent';
import SyncItem from './SyncItem';
import { LoadMoreV2 } from 'components/LoadMore/v2';
import { useProductV2 } from './useProductV2';
import { useERPState } from 'views/Management/ERP/state';
import { useAuth } from 'views/Management/ERP/ERPModal';
import dayjs from 'dayjs';
import StatusButton from 'components/StatusButton';
import { useGetPermission } from 'hooks/business/useNewPermisson';
import ErpProductList from './ErpProductList';
import { useKeyTypePreference } from 'hooks/business/user/useKeyTypePreference';

type KeywordType = ProductListParamsKeywordType | 'ERP_SKU_CODE' | 'ERP_SKU_NAME';

const isNormalKeywordType = (type: KeywordType) =>
  type !== 'ERP_SKU_CODE' && type !== 'ERP_SKU_NAME';

let displayErpTipsState = true;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  height: 52px;
  padding: 0 8px;
  background: #fff;
  border-bottom: solid 1px rgba(0, 0, 0, 0.05);
  .${({ theme }) => theme.getAntdPrefixedClassName('input-search-button')} {
    background: var(--main-color);
    border-color: var(--main-color);
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('input-affix-wrapper')} {
    border-color: #d9d9d9;
    box-shadow: none;
    &:hover,
    &:active,
    &:focus {
      border-color: var(--main-color) !important;
      box-shadow: none;
    }
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('btn-link')} {
    color: var(--main-color);
  }
  .${({ theme }) => theme.getAntdPrefixedClassName('dropdown-menu-item-selected')} {
    background: #fff;
    color: var(--main-color);
  }
`;

export const List = styled.ul`
  margin: 0;
  padding: 0;
  overflow: auto;
  > li {
    padding-bottom: 8px;
  }
`;

const Alert = styled.div`
  display: flex;
  align-items: center;
  padding: 2px 8px;
  background: #fffaf0;
  > div {
    flex: 1 0 0;
    white-space: pre-wrap;
  }
`;

type OrderBy = 'SALE' | 'UPDATE_TIME';

export interface ProductProps extends HTMLAttributes<HTMLDivElement> {
  conversation: Conversation.Conversation;
  showFilter?: boolean;
  relatedId?: string;
  relatedRender?: (product: ReactNode) => ReactNode;
}

const Product = forwardRef<HTMLDivElement, ProductProps>(function Product(props, ref) {
  const {
    conversation,
    showFilter = true,
    relatedId,
    relatedRender = (product) => product,
    ...otherProps
  } = props;
  const { storeId, buyerId, channel, region, ttsToko } = conversation;

  const { getSidebarProductSearchKeywordType, setSidebarProductSearchKeywordType } =
    useKeyTypePreference(toLowerCase(channel));

  const { erpList } = useERPState();
  const { jstErp, qyErp } = useMemo(() => {
    return {
      jstErp: erpList.find(
        (erp) =>
          erp.erpType === 'JUSHUITAN' &&
          erp.storeIds.includes(Number(storeId)) &&
          dayjs(Number(erp.expireTimeStamp)).subtract(7, 'days').isBefore(dayjs()),
      ),
      qyErp: erpList.find(
        (erp) => erp.erpType === 'QIANYI' && erp.storeIds.includes(Number(storeId)),
      ),
    };
  }, [erpList, storeId]);

  const [keywordType, setKeywordType] = useState<KeywordType>(
    ProductListParamsKeywordType.ITEM_NAME,
  );

  const [keywordsState, setKeywordsState] = useState<{ text: string; type: KeywordType }>({
    text: '',
    type: keywordType,
  });
  const keywordsStateRef = useRef({
    text: '',
    type: isNormalKeywordType(keywordType) ? keywordType : ProductListParamsKeywordType.ITEM_NAME,
  });
  if (isNormalKeywordType(keywordsState.type)) {
    keywordsStateRef.current = {
      text: keywordsState.text,
      type: keywordsState.type,
    };
  }

  const [orderby, setOrderby] = useState<OrderBy>(() =>
    channel === 'LAZADA' ? 'UPDATE_TIME' : 'SALE',
  );
  const sellerSKUSuffix = channel === 'LAZADA' ? '(Seller SKU)' : '';
  const { t } = useTranslate();

  const {
    isLoading,
    hasNextPage = false,
    productData,
    fetchNextPage,
    refresh,
  } = useProductV2({
    internal_store_id: storeId,
    external_item_ids: relatedId,
    keyword: keywordsStateRef.current.text,
    order_by: orderby,
    internal_buyer_id: buyerId,
    keyword_type: keywordsStateRef.current.type,
    ttsToko,
  });

  useEffect(() => {
    getSidebarProductSearchKeywordType().then((keywordType) => {
      if (!isNormalKeywordType(keywordType) && !qyErp) {
        setKeywordType(ProductListParamsKeywordType.ITEM_NAME);
      } else {
        setKeywordType(keywordType);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qyErp]);

  useEffect(() => {
    // @ts-ignore
    setSidebarProductSearchKeywordType(keywordType);
  }, [keywordType, setSidebarProductSearchKeywordType]);

  const inputSearch = useMemo(() => {
    const placeholder = isNormalKeywordType(keywordType)
      ? {
          [ProductListParamsKeywordType.ITEM_NAME]: t(
            'scene.chatWorkspace.productTab.search.placeholder.name',
          ),
          [ProductListParamsKeywordType.ITEM_ID]: t(
            'scene.chatWorkspace.productTab.search.placeholder.id',
          ),
          [ProductListParamsKeywordType.SKU]: t(
            'scene.chatWorkspace.productTab.search.placeholder.sku',
          ),
          [ProductListParamsKeywordType.ITEM_NO]: t(
            'scene.chatWorkspace.productTab.search.placeholder.skuCode',
          ),
        }[keywordType]
      : t('common.placeholder.input');
    const normalOptions = [
      {
        value: ProductListParamsKeywordType.ITEM_NAME,
        label: t('domain.product.field.name'),
      },
      { value: ProductListParamsKeywordType.ITEM_ID, label: t('domain.product.field.id') },
      {
        value: ProductListParamsKeywordType.SKU,
        label: 'SKU',
      },
      {
        value: ProductListParamsKeywordType.ITEM_NO,
        label: `${t('domain.product.field.skuCode')}${sellerSKUSuffix}`,
      },
    ];
    return (
      <Input.Search
        className={css`
          .ant-input-search-button {
            padding: 0 8px !important;
          }
        `}
        addonBefore={
          <Select
            dropdownClassName="w-[200px]"
            dropdownMatchSelectWidth={false}
            value={keywordType}
            onChange={(val) => {
              setKeywordType(val);
            }}
            optionLabelProp="label"
            options={
              qyErp
                ? ([
                    {
                      label: t('scene.chatWorkspace.orderTab.productSearch.groupTitle.productInfo'),
                      options: normalOptions,
                    },
                    {
                      label: `ERP ${'商品信息'}`,
                      options: [
                        {
                          value: 'ERP_SKU_CODE',
                          label: t('domain.integration.erp.field.productCode'),
                        },
                        {
                          value: 'ERP_SKU_NAME',
                          label: t('domain.integration.erp.field.productName'),
                        },
                      ],
                    },
                  ] as any)
                : normalOptions
            }
          />
        }
        enterButton
        allowClear
        placeholder={placeholder}
        onSearch={(value) => {
          setKeywordsState({
            text: value,
            type: keywordType,
          });
        }}
      />
    );
  }, [keywordType, sellerSKUSuffix, t, qyErp]);

  const toolbar = showFilter && (
    <Toolbar>
      {inputSearch}
      <Dropdown
        getPopupContainer={(trigger) => trigger}
        overlay={
          <Menu
            selectedKeys={[orderby]}
            onClick={({ key }) => {
              if (key === orderby) {
                return;
              }

              setOrderby(key as 'SALE' | 'UPDATE_TIME');
            }}
          >
            {['SALE', 'UPDATE_TIME'].map((key) => {
              if (key === 'SALE' && channel === 'LAZADA') {
                return null;
              }

              return <Menu.Item key={key}>{t(`orderBy.${key}`)}</Menu.Item>;
            })}
          </Menu>
        }
      >
        <Button type="link">
          {t(`orderBy.${orderby}`)}
          <DownOutlined />
        </Button>
      </Dropdown>
      <SyncItem internalStoreId={storeId} onSuccess={refresh} />
    </Toolbar>
  );

  const getPermission = useGetPermission();
  const permission = getPermission('店铺管理', {
    platform: channel,
    region,
    storeId: Number(storeId),
  });

  const { auth } = useAuth();
  const [displayErpTips, setDisplayErpTips] = useState(displayErpTipsState);

  return (
    <Container ref={ref} {...otherProps}>
      {permission && jstErp && jstErp.erpType === 'JUSHUITAN' && displayErpTips && (
        <Alert>
          <div>
            {dayjs(Number(jstErp.expireTimeStamp)).isBefore(dayjs())
              ? t('scene.chatWorkspace.integrationAccess.erp.expired')
              : t('scene.chatWorkspace.integrationAccess.erp.expiresInDays', {
                  count: dayjs(Number(jstErp.expireTimeStamp)).diff(
                    dayjs().startOf('days'),
                    'days',
                  ),
                })}
          </div>
          <StatusButton
            type="link"
            onClick={() =>
              auth({
                id: jstErp.id,
                erpType: 'JUSHUITAN',
                openLogisticsApi: jstErp.openLogisticsApi,
                itemQueryType: jstErp.itemQueryType,
                reissueItemQueryType: jstErp.reissueItemQueryType,
                storeIds: jstErp.storeIds,
              }).catch(() => {
                message.error(t('common.feedback.fetchFailed'));
              })
            }
          >
            {t('scene.chatWorkspace.orderTab.product.action.authorizeErp')}
            {' >'}
          </StatusButton>
          <Button
            type="text"
            icon={<CloseOutlined />}
            onClick={() => {
              setDisplayErpTips(false);
              displayErpTipsState = false;
            }}
          />
        </Alert>
      )}
      {toolbar}
      <div className="m-0 p-0 flex-grow flex-shrink-0 basis-0 overflow-hidden">
        {isNormalKeywordType(keywordsState.type) ? (
          <LoadMoreV2
            loading={isLoading}
            data={productData}
            hasMore={hasNextPage}
            defaultItemHeight={140}
            onLoadMore={fetchNextPage}
            itemContent={(_, product) => {
              const { externalItemId: id } = product;
              const productNode = (
                <ProductComponent
                  platform={channel}
                  product={product}
                  conversation={conversation}
                />
              );

              if (`${id}` === `${relatedId}`) {
                return <div className="mb-2">{relatedRender(productNode)}</div>;
              }

              return (
                <div className="mb-2" key={id}>
                  {productNode}
                </div>
              );
            }}
            components={{
              EmptyPlaceholder: memo(() => (
                <Empty
                  className="!mt-10"
                  description={
                    keywordsState.text
                      ? t('scene.chatWorkspace.productTab.empty.search')
                      : t('scene.chatWorkspace.productTab.empty.default')
                  }
                />
              )),
            }}
          />
        ) : (
          <ErpProductList
            key={`${keywordsState.type}${keywordsState.text}`}
            storeId={storeId}
            type={keywordsState.type}
            keyword={keywordsState.text}
          />
        )}
      </div>
    </Container>
  );
});

export default memo(Product);
